<script>
  import { onMount } from "svelte";
  import SpotlightGuide from "@components/common/SpotlightGuide.svelte";
  import { registerViewTour } from "lib/stores/viewTourStore.js";
  import { fetchWithSession } from "lib/util/fetchWithSession.js";
  import { projectListStore } from "@stores/damProjectsStore.js";
  import { useUrlParams } from "@stores/urlStore.js";
  import { Modal as DangerModal } from "carbon-components-svelte";
  import Header from "@components/Header.svelte";
  import ProjectStepActions from "../components/ProjectStepActions.svelte";
  import { PROJECT_STAGES } from "@utils/stagesAndSteps.js";
  import PhaseMessageLanguage from "../components/PhaseMessageLanguage.svelte";
  import { START_WHITELIST_MESSAGES } from "./phaseMessages.js";
  import { uiFeatureStore } from "@stores/uiFeatureStore.js";
  import { useNavigate } from "svelte-navigator";
  import { ROUTES } from "@utils/routes.js";
  import {
    InfoBlock,
    TextArea,
    SectionWrapper,
    Button,
  } from "paxray-components-svelte";

  let stepActionsRef;

  // export let isCompleted = false;

  const { markStepAsCompleted } = projectListStore;
  const urlParams = useUrlParams();
  const step = PROJECT_STAGES.SCOPING.steps.START_WHTLST;
  const stage = PROJECT_STAGES.SCOPING.id;
  const navigate = useNavigate();

  let language = "en";
  let message = START_WHITELIST_MESSAGES.en;
  let editingMessage = false;

  let toastMsg;
  let saveSuccessful;
  let openDangerModal = false;
  let showWhitelistGuide = false;
  let whitelistGuideStep = 0;
  let whitelistGuideSteps = [];

  onMount(() =>
    registerViewTour(() => {
      whitelistGuideSteps = [
        {
          target: '[data-guide="start-whitelist-overview"]',
          title: "Find out which apps your team uses",
          text: "This step records which apps and websites your participants work with. No personal data is collected. The result is the basis for your whitelist.",
        },
        {
          target:
            '[data-guide="start-whitelist-language"] .phase_message_language',
          title: "Choose the message language",
          text: "Pick the language your participants read best. Switching the language loads a new template and resets your edits.",
        },
        {
          target: '[data-guide="start-whitelist-message"]',
          title: "Check what participants see",
          text: "Participants see exactly this text when collection starts.",
        },
        {
          target: '[data-guide="start-whitelist-edit"]',
          title: "Adjust the message",
          text: "Click Edit message to change the text, for example to add a personal note or a contact person. Click Done editing to see the preview again.",
        },
        {
          target:
            '[data-guide="start-whitelist-action"] > button, [data-guide="start-whitelist-action"] > .completed-badge',
          title: "Start collection",
          text: "Click Start Whitelist Data Collection and confirm. We recommend collecting for at least seven days. Afterwards you can end it and review the apps with your team.",
        },
      ].filter(({ target }) => {
        const element = document.querySelector(target);
        return (
          element &&
          getComputedStyle(element).visibility === "visible" &&
          element.getBoundingClientRect().width > 0 &&
          element.getBoundingClientRect().height > 0
        );
      });
      whitelistGuideStep = 0;
      showWhitelistGuide = whitelistGuideSteps.length > 0;
    }),
  );

  $: ({ idproject } = $urlParams);
  $: ({ currentProjectContacts } = $projectListStore);
  $: projectProgressHref = `${ROUTES.dam.progress}?${new URLSearchParams({ idproject }).toString()}`;

  async function handleMarkAsCompleted() {
    const data = await markStepAsCompleted(idproject, step, stage);
    if (data?.ok) {
      stepActionsRef?.refresh();
    }
    return data;
  }

  async function startWhitelistPhase() {
    /*********************** NEW VERSION *****************************************/

    const res = await fetchWithSession(
      `/api/damprojects/startScopingRecording`,
      {
        method: "POST",
        body: JSON.stringify({
          idproject: idproject,
          message: message,
          selectedPresetIds: [], // to be removed when backend is updated
        }),
      },
    );
    const data = await res.json();
    if (data?.ok) {
      const completion = await handleMarkAsCompleted();
      if (completion?.ok) {
        navigate(`${ROUTES.dam.progress}?idproject=${idproject}`);
      }
    }
    openDangerModal = false;
  }

  $$restProps;
</script>

<Header
  section="Project"
  area="Scoping"
  breadcrumb="Start Whitelist Data Collection"
  areaHref={`${projectProgressHref}#scoping-start`}
  tooltipText={"Start collecting the apps and websites your team uses and show participants the message below.\nWe recommend collecting for at least seven days.\nStarting cannot be undone."}
>
  {#if $uiFeatureStore.projectProgress.startWhitelistPhase}
    <div data-guide="start-whitelist-action">
      <ProjectStepActions
        bind:this={stepActionsRef}
        {idproject}
        {step}
        {stage}
        showGoBackButton={false}
        text="Start Whitelist Data Collection"
        confirmBeforeComplete={false}
        onClick={() => (openDangerModal = true)}
      />
    </div>
  {/if}
  <!-- <ButtonNew
    text="Start Whitelist Phase"
    setting={true}
    secondary={true}
    on:click={() => (openDangerModal = true)}
  /> -->
</Header>

<SectionWrapper fullscreen={true} seamless={true} seamlessPadding={true}>
  <div class="main" slot="main">
    <div data-guide="start-whitelist-overview">
      <InfoBlock
        type="info"
        heading="Start Whitelist Data Collection"
        message="The message below is shown to participants. The apps and websites they use are then collected. We recommend at least seven days, so apps used only once a week are included."
        showClose={false}
      />
    </div>
    <div class="row">
      <div class="cell">
        <div class="field_label" data-guide="start-whitelist-language">
          <PhaseMessageLanguage
            messages={START_WHITELIST_MESSAGES}
            bind:language
            bind:value={message}
          />
          <div data-guide="start-whitelist-edit">
            <Button
              size="field"
              kind="secondary"
              on:click={() => (editingMessage = !editingMessage)}
            >
              {editingMessage ? "Done editing" : "Edit message"}
            </Button>
          </div>
        </div>
        <div data-guide="start-whitelist-message">
          {#if editingMessage}
            <TextArea
              placeholder="Enter a message for the users"
              bind:value={message}
              richText
              persons={currentProjectContacts}
            />
          {:else}
            <div class="message-preview">
              {@html message}
            </div>
          {/if}
        </div>
      </div>
    </div>
  </div></SectionWrapper
>

<SpotlightGuide
  bind:open={showWhitelistGuide}
  bind:currentStep={whitelistGuideStep}
  steps={whitelistGuideSteps}
  allowInteraction={false}
/>

<DangerModal
  danger
  bind:open={openDangerModal}
  preventCloseOnClickOutside
  modalHeading="Start Whitelist Data Collection?"
  primaryButtonText="Start Whitelist Data Collection"
  secondaryButtonText="Cancel"
  on:click:button--primary={startWhitelistPhase}
  on:click:button--secondary={() => (openDangerModal = false)}
>
  <p class="deleteModalBody">
    Start collecting apps and websites for the whitelist and show the
    participant message. We recommend collecting for at least seven days, so
    apps used only once a week are included. You can end collection earlier if
    needed. This action cannot be undone.
  </p>
</DangerModal>

<style>
  .field_label {
    display: flex;
    align-items: flex-end;
    gap: var(--xs);
    margin-bottom: var(--control-gap);
  }

  .field_label :global(.phase_message_language) {
    margin-left: 0;
  }

  .message-preview {
    padding: 1rem;
    border: 1px solid var(--border-color);
    border-radius: 0.25rem;
    overflow-wrap: anywhere;
  }

  .main {
    max-width: 100%;
    width: 100%;
    height: 100%;
    min-height: 0;
    display: flex;
    flex-direction: column;
    gap: var(--control-gap);
  }

  .row {
    display: flex;
    flex-direction: row;
    gap: 2rem;
    width: 100%;
    flex: 1 1 auto;
    min-height: 0;
  }
  .cell {
    flex: 1 1 auto;
    width: 50%;
    height: 100%;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }

  .message-preview :global(b),
  .message-preview :global(strong) {
    font-weight: bold;
  }

  .message-preview :global(i),
  .message-preview :global(em) {
    font-style: italic;
  }

  .message-preview :global(u) {
    text-decoration: underline;
  }
</style>
